import type { ChartProgressGroup, ChartProject } from './types';
import { format, isAfter, isBefore, parse, subDays } from 'date-fns';
import { resetTime } from '@/utils/formatting';
import { PROGRESS_DATA_DATE_FORMAT } from './constants';
import { type RetainerPeriod } from '@/generated/graphql';

const parseGroupNameDate = (date: string) => {
	return parse(date, PROGRESS_DATA_DATE_FORMAT, new Date());
};
export const getFormattedProgressData = (
	project?: ChartProject
): ChartProgressGroup[] => {
	if (!project?.progressData?.length) {
		return [];
	}
	const oneDayBeforeProgressStarts = subDays(
		parseGroupNameDate(project.progressData[0].groupName),
		1
	);
	return [
		// Add another day in front of the chart so that the graph lines always
		// start at 0
		{
			groupName: format(
				oneDayBeforeProgressStarts,
				PROGRESS_DATA_DATE_FORMAT
			),
			actualValue: 0,
			budgetValue: 0,
			forecastedValue: 0,
			plannedValue: 0,
			actualRevenue: 0,
			forecastedRevenue: 0,
			monetaryBudget: 0,
			bookingsCost: 0,
			costBudget: 0,
			forecastedCost: 0,
			tasksCost: 0,
			actualCost: 0,
		},
		...project.progressData,
	];
};

export const getFirstProgressGroupDate = (progress?: ChartProgressGroup[]) => {
	return progress?.length
		? parseGroupNameDate(progress[0].groupName)
		: undefined;
};

export const getTodaysDateIndex = (progress?: ChartProgressGroup[]) => {
	const TODAY = resetTime(new Date(Date.now()));
	const TODAY_IN_PROGRESS_DATA_FORMAT = format(
		TODAY,
		PROGRESS_DATA_DATE_FORMAT
	);
	let index = -1;
	if (progress) {
		index = progress.findIndex(
			(progressGroup) =>
				progressGroup.groupName === TODAY_IN_PROGRESS_DATA_FORMAT
		);
	}
	return index !== -1 ? index : 0;
};

export const getIsTodayBeforeProgress = (progress?: ChartProgressGroup[]) => {
	const TODAY = resetTime(new Date(Date.now()));
	const firstProgressGroupDate = getFirstProgressGroupDate(progress);
	if (!firstProgressGroupDate) {
		return false;
	}
	return isBefore(TODAY, firstProgressGroupDate);
};

export const getIsTodayAfterProgress = (progress?: ChartProgressGroup[]) => {
	const TODAY = resetTime(new Date(Date.now()));
	if (!!progress) {
		const lastProgressGroup = progress[progress.length - 1];
		if (!lastProgressGroup) {
			return false;
		}

		return isAfter(TODAY, parseGroupNameDate(lastProgressGroup?.groupName));
	}
	return false;
};

export const getChartStartTime = (progress?: ChartProgressGroup[]) => {
	return getFirstProgressGroupDate(progress)?.getTime();
};

export const getStartMarkerTime = (
	project?: ChartProject,
	progress?: ChartProgressGroup[],
	retainerPeriodId?: RetainerPeriod['id']
) => {
	const TODAY = resetTime(new Date(Date.now()));
	const firstProgressGroupDate = getFirstProgressGroupDate(progress);
	if ((retainerPeriodId || !project?.date) && firstProgressGroupDate) {
		return firstProgressGroupDate?.getTime();
	}

	if (!project?.date) {
		return TODAY.getTime();
	}

	return resetTime(new Date(project.date)).getTime();
};

export const getForecastedStartTime = (progress?: ChartProgressGroup[]) => {
	const TODAY = resetTime(new Date(Date.now()));
	const todaysDateIndex = getTodaysDateIndex(progress);
	const chartStartTime = getChartStartTime(progress);

	if (!todaysDateIndex) {
		return chartStartTime ?? TODAY.getTime();
	}
	let progressGroup = undefined;
	if (progress) {
		const firstProgressGroup = progress[0];
		progressGroup = progress[todaysDateIndex] ?? firstProgressGroup;
	}

	return parseGroupNameDate(progressGroup?.groupName ?? '').getTime();
};

export const getEndMarkerTime = (
	project?: ChartProject,
	progress?: ChartProgressGroup[],
	retainerPeriodId?: RetainerPeriod['id']
) => {
	if (progress?.length && (retainerPeriodId || !project?.deadline)) {
		return parseGroupNameDate(
			progress[progress.length - 1].groupName
		).getTime();
	}

	if (!project?.deadline) {
		return undefined;
	}
	return resetTime(new Date(project.deadline)).getTime();
};

export const getYValues = (arr: { x: number; y: number }[]): number[] => {
	return arr.map((point) => point.y);
};

export const getLastYValue = (
	arr: { x: number; y: number }[]
): number | undefined => {
	return arr.length ? arr[arr.length - 1].y : undefined;
};
